// OHLC candlestick chart using Lightweight Charts v5.
// Always renders the container div. Messages overlay on top.

import { useEffect, useRef } from "react";
import { createChart, ColorType, CandlestickSeries } from "lightweight-charts";
import type { BarDto } from "../api/types";

interface Props {
  bars: BarDto[];
  loading?: boolean;
}

export function OHLCChart({ bars, loading }: Props) {
  const containerRef = useRef<HTMLDivElement>(null);
  const chartRef = useRef<ReturnType<typeof createChart> | null>(null);
  const seriesRef = useRef<ReturnType<ReturnType<typeof createChart>["addSeries"]> | null>(null);

  // Create chart once — always has a container div
  useEffect(() => {
    if (!containerRef.current) return;
    const chart = createChart(containerRef.current, {
      layout: {
        background: { type: ColorType.Solid, color: "#0B0C10" },
        textColor: "#6B7280",
        fontSize: 10,
        fontFamily: "SF Mono, Cascadia Code, Consolas, monospace",
      },
      grid: {
        vertLines: { color: "#1E2128" },
        horzLines: { color: "#1E2128" },
      },
      timeScale: {
        borderColor: "#282B33",
        timeVisible: true,
        secondsVisible: false,
      },
      rightPriceScale: { borderColor: "#282B33" },
      handleScroll: { vertTouchDrag: false },
      width: containerRef.current.clientWidth,
      height: containerRef.current.clientHeight,
      autoSize: true,
    });

    const series = chart.addSeries(CandlestickSeries, {
      upColor: "#00E676",
      downColor: "#FF5252",
      borderUpColor: "#00E676",
      borderDownColor: "#FF5252",
      wickUpColor: "#00E676",
      wickDownColor: "#FF5252",
    });

    chartRef.current = chart;
    seriesRef.current = series;

    const observer = new ResizeObserver(() => {
      if (containerRef.current) {
        chart.applyOptions({ width: containerRef.current.clientWidth, height: containerRef.current.clientHeight });
      }
    });
    observer.observe(containerRef.current);

    return () => {
      observer.disconnect();
      chart.remove();
      chartRef.current = null;
      seriesRef.current = null;
    };
  }, []);

  // Update data on change
  useEffect(() => {
    if (!seriesRef.current || bars.length === 0) return;
    const data = bars.map((b) => ({
      time: (new Date(b.ts).getTime() / 1000) as any,
      open: b.open,
      high: b.high,
      low: b.low,
      close: b.close,
    }));
    seriesRef.current.setData(data);
    chartRef.current?.timeScale().fitContent();
  }, [bars]);

  return (
    <div style={{ width: "100%", height: "100%", position: "relative", minHeight: 0 }}>
      {(loading || bars.length === 0) && (
        <div style={{
          position: "absolute", inset: 0, display: "flex", alignItems: "center",
          justifyContent: "center", color: "var(--fg-muted)", fontSize: 11, zIndex: 10,
          background: "rgba(11,12,16,0.8)",
        }}>
          {loading ? "Loading bars..." : "No sleeve-specific bars yet — chart shows market data when available"}
        </div>
      )}
      <div ref={containerRef} style={{ width: "100%", height: "100%", minHeight: 0 }} />
    </div>
  );
}